import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import {
  parseBulkCsv,
  toSubmitRows,
  createBulkInviteJob,
  MAX_BULK_ROWS,
  type BulkCsvParseResult,
} from '../../api/bulkInviteApi';

/**
 * WL-K5b — Bulk-Onboarding: eine Kanzlei lädt 50–500 Mandanten per CSV ein.
 *
 * Ablauf: CSV wählen → clientseitige Vorschau (ungültige Zeilen, Datei-Dubletten,
 * Zeilenlimit) → Pflicht-Checkbox `advisorConfirmed` → `createBulkInviteJob`. Der
 * Server re-validiert ALLES; die Vorschau ist reiner Komfort. Nach dem Absenden
 * zeigt das Panel die Live-Status-Tabelle (Job-Doc + `rows`-Subcollection direkt aus
 * Firestore, gegated über die Row-`advisorUid`-Regel).
 *
 * Die Sichtbarkeit dieses Panels ist im Parent kosmetisch über
 * `canUseClientInvite` (M-GA-4; `GA_FLAG || isLoopBBeta`) gegated; die harte
 * fail-closed-Sperre (Beta + Operator-Vetting + AVV) bleibt server-seitig.
 */

/** Live job document shape (subset read for the status view). */
interface BulkJobDoc {
  status: string;
  totalRows: number;
  cursor: number;
  counts?: {
    invited?: number;
    accepted?: number;
    failed?: number;
    skippedDuplicate?: number;
    pending?: number;
  };
  sourceFilename?: string | null;
}

/** Live row document shape (subset read for the status table). */
interface BulkRowDoc {
  rowIndex: number;
  emailNorm: string;
  clientName: string | null;
  status: string;
  error: string | null;
}

const PREVIEW_LIMIT = 50;

const BulkInvitePanel: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [parse, setParse] = useState<BulkCsvParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [advisorConfirmed, setAdvisorConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<BulkJobDoc | null>(null);
  const [rows, setRows] = useState<BulkRowDoc[]>([]);

  // ── CSV lesen + parsen ──────────────────────────────────────────────────────
  const handleFile = useCallback(
    async (file: File) => {
      setParseError(null);
      setSubmitError(null);
      setParse(null);
      setFileName(file.name);
      try {
        const text = await file.text();
        const result = parseBulkCsv(text);
        if (result.rows.length === 0) {
          setParseError(t('clientInvite.bulk.emptyFile'));
          return;
        }
        setParse(result);
      } catch (err) {
        logger.error('[BulkInvitePanel] CSV parse failed', err);
        setParseError(t('clientInvite.bulk.parseError'));
      }
    },
    [t],
  );

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const resetSelection = () => {
    setParse(null);
    setFileName(null);
    setParseError(null);
    setSubmitError(null);
    setAdvisorConfirmed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Absenden ────────────────────────────────────────────────────────────────
  const canSubmit =
    !!currentUser &&
    !!parse &&
    parse.validUniqueCount > 0 &&
    !parse.overLimit &&
    advisorConfirmed &&
    !submitting;

  const handleSubmit = async () => {
    if (!parse) return;
    setSubmitError(null);
    if (parse.overLimit) {
      setSubmitError(t('clientInvite.bulk.overLimit', { max: MAX_BULK_ROWS }));
      return;
    }
    setSubmitting(true);
    try {
      const submitRows = toSubmitRows(parse);
      const res = await createBulkInviteJob(submitRows, advisorConfirmed, fileName);
      setJobId(res.jobId);
    } catch (err) {
      logger.error('[BulkInvitePanel] createBulkInviteJob failed', err);
      const code = (err as { code?: string })?.code;
      setSubmitError(
        code === 'permission-denied'
          ? t('clientInvite.bulk.notVetted')
          : code === 'failed-precondition'
            ? t('clientInvite.bulk.activeJobExists')
            : t('clientInvite.bulk.submitError'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Live-Status (Job + rows) ──────────────────────────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    const unsubJob = onSnapshot(
      doc(db, 'bulk_invite_jobs', jobId),
      (snap) => {
        if (snap.exists()) setJob(snap.data() as BulkJobDoc);
      },
      (err) => logger.error('[BulkInvitePanel] job snapshot failed', err),
    );
    // Equality-only (kein Composite-Index): rowIndex wird clientseitig sortiert.
    const rowsQuery = query(
      collection(db, 'bulk_invite_jobs', jobId, 'rows'),
      where('advisorUid', '==', currentUser?.uid ?? '__none__'),
    );
    const unsubRows = onSnapshot(
      rowsQuery,
      (snap) => {
        const next = snap.docs.map((d) => d.data() as BulkRowDoc);
        next.sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));
        setRows(next);
      },
      (err) => logger.error('[BulkInvitePanel] rows snapshot failed', err),
    );
    return () => {
      unsubJob();
      unsubRows();
    };
  }, [jobId, currentUser?.uid]);

  const rowStatusLabel = (status: string): string => {
    switch (status) {
      case 'invited':
        return t('clientInvite.bulk.rowInvited');
      case 'accepted':
        return t('clientInvite.bulk.rowAccepted');
      case 'skipped_duplicate':
        return t('clientInvite.bulk.rowSkipped');
      case 'failed':
        return t('clientInvite.bulk.rowFailed');
      case 'declined':
        return t('clientInvite.bulk.rowDeclined');
      default:
        return t('clientInvite.bulk.rowPending');
    }
  };

  const rowStatusClass = (status: string): string => {
    switch (status) {
      case 'accepted':
        return 'text-[var(--tone-success-fg)]';
      case 'invited':
        return 'text-accent-ink';
      case 'failed':
        return 'text-[var(--tone-danger-fg)]';
      case 'skipped_duplicate':
      case 'declined':
        return 'text-muted';
      default:
        return 'text-faint';
    }
  };

  // ── Ansicht: Live-Status (nach dem Absenden) ─────────────────────────────────
  if (jobId) {
    const c = job?.counts ?? {};
    return (
      <div>
        <h3 className="text-sm font-semibold text-primary mb-1">{t('clientInvite.bulk.statusTitle')}</h3>
        {job?.sourceFilename && (
          <p className="text-xs text-faint mb-3">{job.sourceFilename}</p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <StatTile label={t('clientInvite.bulk.rowPending')} value={c.pending ?? 0} tone="text-secondary" />
          <StatTile label={t('clientInvite.bulk.rowInvited')} value={c.invited ?? 0} tone="text-accent-ink" />
          <StatTile label={t('clientInvite.bulk.rowAccepted')} value={c.accepted ?? 0} tone="text-[var(--tone-success-fg)]" />
          <StatTile label={t('clientInvite.bulk.rowSkipped')} value={c.skippedDuplicate ?? 0} tone="text-muted" />
          <StatTile label={t('clientInvite.bulk.rowFailed')} value={c.failed ?? 0} tone="text-[var(--tone-danger-fg)]" />
        </div>

        <p className="text-xs text-muted mb-3">
          {t('clientInvite.bulk.jobStatus', {
            status: t(`clientInvite.bulk.jobState.${job?.status ?? 'validated'}`, job?.status ?? ''),
          })}
          {' · '}
          {t('clientInvite.bulk.progress', { done: job?.cursor ?? 0, total: job?.totalRows ?? rows.length })}
        </p>

        <div className="max-h-96 overflow-y-auto rounded-md border border-hairline">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-surface text-muted">
              <tr>
                <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colEmail')}</th>
                <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colName')}</th>
                <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colStatus')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border-hairline)]">
              {rows.map((r) => (
                <tr key={r.rowIndex} className="text-secondary">
                  <td className="px-2 py-1.5">{r.emailNorm}</td>
                  <td className="px-2 py-1.5 text-muted">{r.clientName ?? '—'}</td>
                  <td className={`px-2 py-1.5 ${rowStatusClass(r.status)}`}>{rowStatusLabel(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          onClick={() => {
            setJobId(null);
            setJob(null);
            setRows([]);
            resetSelection();
          }}
          className="mt-4 inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-primary hover:bg-surface-hover"
        >
          {t('clientInvite.bulk.newImport')}
        </button>
      </div>
    );
  }

  // ── Ansicht: Upload + Vorschau ───────────────────────────────────────────────
  const previewRows = parse ? parse.rows.slice(0, PREVIEW_LIMIT) : [];

  return (
    <div>
      <p className="text-xs text-muted mb-3">{t('clientInvite.bulk.intro')}</p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onFileChange}
        disabled={submitting}
        className="block w-full text-xs text-secondary file:mr-3 file:rounded-md file:border-0 file:bg-indigo-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-indigo-500 disabled:opacity-50"
      />
      <p className="mt-1 text-[11px] text-faint">{t('clientInvite.bulk.formatHint')}</p>

      {parseError && <p className="mt-2 text-xs text-[var(--tone-danger-fg)]">{parseError}</p>}

      {parse && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-3 text-xs mb-3">
            <span className="text-[var(--tone-success-fg)]">
              {t('clientInvite.bulk.summaryValid', { count: parse.validUniqueCount })}
            </span>
            {parse.duplicateCount > 0 && (
              <span className="text-muted">
                {t('clientInvite.bulk.summaryDuplicate', { count: parse.duplicateCount })}
              </span>
            )}
            {parse.invalidCount > 0 && (
              <span className="text-[var(--tone-danger-fg)]">
                {t('clientInvite.bulk.summaryInvalid', { count: parse.invalidCount })}
              </span>
            )}
          </div>

          {parse.overLimit && (
            <p className="mb-3 rounded-md bg-[var(--tone-danger-bg)] px-3 py-2 text-xs text-[var(--tone-danger-fg)]">
              {t('clientInvite.bulk.overLimit', { max: MAX_BULK_ROWS })}
            </p>
          )}

          <div className="max-h-72 overflow-y-auto rounded-md border border-hairline">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-surface text-muted">
                <tr>
                  <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colLine')}</th>
                  <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colEmail')}</th>
                  <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colName')}</th>
                  <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colNr')}</th>
                  <th className="px-2 py-1.5 font-medium">{t('clientInvite.bulk.colStatus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border-hairline)]">
                {previewRows.map((r) => {
                  const state = !r.valid
                    ? { label: t('clientInvite.bulk.previewInvalid'), cls: 'text-[var(--tone-danger-fg)]' }
                    : r.duplicate
                      ? { label: t('clientInvite.bulk.previewDuplicate'), cls: 'text-muted' }
                      : { label: t('clientInvite.bulk.previewOk'), cls: 'text-[var(--tone-success-fg)]' };
                  return (
                    <tr key={r.lineNumber} className="text-secondary">
                      <td className="px-2 py-1.5 text-faint">{r.lineNumber}</td>
                      <td className="px-2 py-1.5">{r.email || '—'}</td>
                      <td className="px-2 py-1.5 text-muted">{r.clientName ?? '—'}</td>
                      <td className="px-2 py-1.5 text-muted">{r.mandantenNr ?? '—'}</td>
                      <td className={`px-2 py-1.5 ${state.cls}`}>{state.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {parse.rows.length > PREVIEW_LIMIT && (
            <p className="mt-1 text-[11px] text-faint">
              {t('clientInvite.bulk.previewTruncated', { shown: PREVIEW_LIMIT, total: parse.rows.length })}
            </p>
          )}

          <label className="mt-3 flex items-start gap-2 text-[12px] leading-relaxed text-secondary">
            <input
              type="checkbox"
              checked={advisorConfirmed}
              onChange={(e) => setAdvisorConfirmed(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500"
            />
            <span>{t('clientInvite.bulk.confirmLabel')}</span>
          </label>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting
                ? t('clientInvite.bulk.submitting')
                : t('clientInvite.bulk.submitButton', { count: parse.validUniqueCount })}
            </button>
            <button
              type="button"
              onClick={resetSelection}
              disabled={submitting}
              className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-primary hover:bg-surface-hover disabled:opacity-50"
            >
              {t('clientInvite.bulk.reset')}
            </button>
          </div>

          {submitError && <p className="mt-2 text-xs text-[var(--tone-danger-fg)]">{submitError}</p>}
        </div>
      )}
    </div>
  );
};

/** Small labelled stat tile for the live status summary. */
const StatTile: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => (
  <div className="rounded-md bg-surface border border-hairline px-2 py-1.5">
    <div className={`text-lg font-semibold ${tone}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-faint">{label}</div>
  </div>
);

export default BulkInvitePanel;
